import { useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';

import { registerUser, loginUser } from '../api/auth.api';
import { setCredentials, setBrokerStatus } from '@store/slices/authSlice';
import { fetchBrokerList } from '@store/slices/brokerSlice';
import { setLocal, getLocal, removeLocal } from '@utils/storage';
import { toast } from '@/components/ui/toast/Toast';


const SESSION_KEY = 'user';
const SELECTED_PLAN_KEY = 'selectedPlan';
const CURRENT_PAGE_KEY = 'current-page';

export const useRegister = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch();

    const [loading, setLoading] = useState(false);
    const [requestError, setRequestError] = useState('');

    const googleData = location.state ?? null;
    const isGoogleRegister = !!googleData?.gToken;

    const handlePostLoginRedirect = useCallback(({ activeSubscription, activeTradeSubscription }) => {
        const selectedPlan = getLocal(SELECTED_PLAN_KEY)?.plan;

        if (activeSubscription || activeTradeSubscription) {
            if (selectedPlan) {
                removeLocal(SELECTED_PLAN_KEY);
                navigate('/pricing');
                return;
            }
            if (activeTradeSubscription && !activeSubscription) {
                navigate('/trading/broker-accounts');
                return;
            }
            navigate(getLocal(CURRENT_PAGE_KEY)?.pathname || '/pages/charts');
        } else {
            navigate('/pricing');
        }
    }, [navigate]);

    const syncBrokerStatus = useCallback(async (userData) => {
        const result = await dispatch(fetchBrokerList());
        const hasBroker = !!result?.payload?.length;
        dispatch(setBrokerStatus(hasBroker));
        setLocal(SESSION_KEY, { ...userData, brokerLoggedIn: hasBroker });
    }, [dispatch]);

    // After Google register — auto login with gToken
    const handleGooglePostRegister = useCallback(async () => {
        const data = await loginUser({ email: '', password: '', gtoken: googleData.gToken });

        if (data?.code === 200) {
            const payload = { ...data.data, isLoggedIn: true };
            setLocal(SESSION_KEY, payload);
            dispatch(setCredentials(payload));
            syncBrokerStatus(payload);
            handlePostLoginRedirect(payload);
        } else {
            toast.info('Registration successful. Please sign in.');
            navigate('/login');
        }
    }, [googleData, dispatch, syncBrokerStatus, handlePostLoginRedirect, navigate]);

    const handleRegister = useCallback(async (values) => {
        setLoading(true);
        setRequestError('');

        const data = await registerUser(values);

        if (data?.code === 200) {
            if (isGoogleRegister) {
                await handleGooglePostRegister();
            } else {
                navigate('/authentication/verify-email', { state: { email: values.email } });
            }
        } else {
            setRequestError(data?.error || 'Registration failed. Please try again.');
        }

        setLoading(false);
    }, [isGoogleRegister, handleGooglePostRegister, navigate]);

    return {
        loading,
        requestError,
        isGoogleRegister,
        googleData,
        handleRegister,
    };
};